import * as React from 'react';
import { useRef } from 'react';

import { IconButton } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import SparkleOpenIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleOpenIcon';
import { useMobileWebConversationalDiscoveryExperiment } from 'bundles/unified-common/contexts/MobileWebConversationalDiscoveryExperimentContext';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/composable-page-header';

const MobileWebChatTriggerGridItem = () => {
  const { mobileWebConversationalDiscoveryEnabled } = useMobileWebConversationalDiscoveryExperiment();
  const { coachOpen, setCoachOpen, isFullScreen, setIsFullScreen, contextConfig, setTriggerElementRef } =
    useCoachState();
  const track = useTracker();
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!mobileWebConversationalDiscoveryEnabled) return null;

  // coachOpen can still be false for one render while isFullScreen is true (the container
  // re-arms isFullScreen in an effect after its own close), so neither flag alone describes
  // what's on screen. Gate the affordance on both so the button never offers to close a tray
  // the learner can't see.
  const isTrayExpanded = isFullScreen && coachOpen;

  const tooltip = isTrayExpanded ? _t('Close Ask Coursera AI') : _t('Ask Coursera AI');

  const handleClick = () => {
    track('interact_coach', {
      coachInteractionType: isTrayExpanded ? 'close_coach_tool' : 'launch_coach_tool',
      coach: {
        mode: contextConfig.config.coachModeId,
        location: 'page_header',
      },
      coachAction: contextConfig.config.coachActionId,
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: isTrayExpanded ? 'close_coach_tool' : 'launch_coach_tool',
      coach: {
        mode: contextConfig.config.coachModeId,
        location: 'page_header',
      },
      coachAction: contextConfig.config.coachActionId,
    });

    // Lets the tray focus this button back on close, since its FocusTrap doesn't return focus itself.
    setTriggerElementRef(buttonRef.current);
    if (isTrayExpanded) {
      setIsFullScreen(false);
    } else {
      if (!coachOpen) setCoachOpen(true);
      setIsFullScreen(true);
    }
  };

  return (
    <IconButton
      ref={buttonRef}
      aria-label={tooltip}
      tooltip={tooltip}
      onClick={handleClick}
      data-testid="mobile-web-chat-trigger"
      variant="ghost"
      // Drops the button's own hit-box padding (48px -> 24px, still WCAG 2.2 AA's 24px target-size
      // floor) to match the tight icon-row spacing in the Figma reference (MERCH-3449). This
      // component only renders in the treatment, so control's icon spacing is untouched.
      edgeAlign="noPadding"
      icon={isTrayExpanded ? <SparkleOpenIcon size="large" /> : <AIGenerateBrandedNewIcon size="large" />}
    />
  );
};

export default MobileWebChatTriggerGridItem;
